import React, { useState } from 'react';
import { FileDown, Building2, Calendar, Clock, IndianRupee } from 'lucide-react';
import { jsPDF } from 'jspdf';
import DateInput from './DateInput';
import './AppointmentOrderForm.css';

const mockFormData = {
  establishmentName: "ABC Manufacturing Pvt Ltd",
  month: "July",
  year: "2025",
  overtimeRate: "2x",

  employees: [
    { slNo: "1", name: "Rajesh Kumar", designation: "Operator", normalHours: "208", overtimeHours: "12", hourlyRate: "135", overtimePay: "3240" },
    { slNo: "2", name: "Anita Sharma", designation: "Inspector", normalHours: "208", overtimeHours: "8", hourlyRate: "120", overtimePay: "1920" },
    { slNo: "3", name: "Suresh Patel", designation: "Supervisor", normalHours: "208", overtimeHours: "15", hourlyRate: "154", overtimePay: "4620" }
  ],

  preparedBy: "HR Manager",
  approvedBy: "Production Manager",
  place: "Bangalore",
  date: "2025-07-31"
};

const FormXXIIIOvertime = () => {
  const [formData, setFormData] = useState(mockFormData);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEmployeeChange = (index, field, value) => {
    const updatedEmployees = [...formData.employees];
    updatedEmployees[index] = { ...updatedEmployees[index], [field]: value };
    setFormData(prev => ({ ...prev, employees: updatedEmployees }));
  };

  const generatePDF = async () => {
    setIsGenerating(true);

    try {
      const doc = new jsPDF('landscape');

      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('FORM XXIII', 148, 15, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text('REGISTER OF OVERTIME WORK AND PAYMENTS', 148, 22, { align: 'center' });

      let yPos = 35;
      const leftMargin = 15;

      doc.setFontSize(9);
      doc.text('Organization:', leftMargin, yPos);
      doc.setFont(undefined, 'bold');
      doc.text(formData.establishmentName, leftMargin + 35, yPos);
      yPos += 5;

      doc.setFont(undefined, 'normal');
      doc.text('Period:', leftMargin, yPos);
      doc.setFont(undefined, 'bold');
      doc.text(`${formData.month} ${formData.year}`, leftMargin + 20, yPos);
      yPos += 5;

      doc.setFont(undefined, 'normal');
      doc.text('Overtime Rate:', leftMargin, yPos);
      doc.setFont(undefined, 'bold');
      doc.text(formData.overtimeRate, leftMargin + 32, yPos);
      yPos += 8;

      // Table
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      const cols = {
        slNo: leftMargin,
        name: leftMargin + 12,
        designation: leftMargin + 60,
        normalHrs: leftMargin + 105,
        otHrs: leftMargin + 145,
        hourlyRate: leftMargin + 180,
        otPay: leftMargin + 225
      };

      doc.text('Sl', cols.slNo, yPos);
      doc.text('Name', cols.name, yPos);
      doc.text('Designation', cols.designation, yPos);
      doc.text('Normal Hrs', cols.normalHrs, yPos);
      doc.text('OT Hours', cols.otHrs, yPos);
      doc.text('Hourly Rate', cols.hourlyRate, yPos);
      doc.text('OT Payment', cols.otPay, yPos);

      yPos += 2;
      doc.line(leftMargin, yPos, 282, yPos);
      yPos += 5;

      doc.setFont(undefined, 'normal');
      formData.employees.forEach((emp) => {
        doc.text(emp.slNo, cols.slNo, yPos);
        doc.text(emp.name.substring(0, 25), cols.name, yPos);
        doc.text(emp.designation, cols.designation, yPos);
        doc.text(emp.normalHours, cols.normalHrs, yPos);
        doc.text(emp.overtimeHours, cols.otHrs, yPos);
        doc.text(`Rs. ${emp.hourlyRate}`, cols.hourlyRate, yPos);
        doc.text(`Rs. ${emp.overtimePay}`, cols.otPay, yPos);
        yPos += 5;
      });

      yPos += 3;
      doc.line(leftMargin, yPos, 282, yPos);
      yPos += 10;

      doc.setFontSize(8);
      doc.text('Prepared By: ' + formData.preparedBy, leftMargin, yPos);
      doc.text('Approved By: ' + formData.approvedBy, leftMargin + 100, yPos);
      yPos += 6;
      doc.text('Place: ' + formData.place, leftMargin, yPos);
      doc.text('Date: ' + formData.date, leftMargin + 100, yPos);

      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 148, 200, { align: 'center' });
      doc.text('PayrollGen - Form XXIII Overtime Register', 148, 205, { align: 'center' });

      doc.save(`FormXXIII_Overtime_${formData.month}_${formData.year}.pdf`);

      setTimeout(() => {
        setIsGenerating(false);
        alert('Form XXIII - Overtime Register PDF generated successfully!');
      }, 500);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
      setIsGenerating(false);
    }
  };

  return (
    <div className="appointment-order-container">
      <div className="appointment-header">
        <div>
          <h2 className="appointment-title">Form XXIII - Overtime Register</h2>
          <p className="appointment-subtitle">Register of Overtime Work and Payments</p>
        </div>
      </div>

      <div className="appointment-content">
        <div className="info-alert">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <span>Review the overtime work details below.</span>
        </div>

        <div className="form-section">
          <div className="section-header">
            <Building2 size={20} />
            <h3>Organization & Period</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Organization Name</label>
              <input type="text" value={formData.establishmentName} onChange={(e) => handleInputChange('establishmentName', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Month</label>
              <select value={formData.month} onChange={(e) => handleInputChange('month', e.target.value)}>
                {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Year</label>
              <input type="number" value={formData.year} onChange={(e) => handleInputChange('year', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Overtime Rate</label>
              <input type="text" value={formData.overtimeRate} onChange={(e) => handleInputChange('overtimeRate', e.target.value)} placeholder="e.g., 2x, 1.5x" />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <Clock size={20} />
            <h3>Employee Overtime Details</h3>
          </div>

          {formData.employees.map((emp, idx) => (
            <div key={idx} style={{ background: 'var(--theme-surface)', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #e5e7eb' }}>
              <div className="form-grid">
                <div className="form-field">
                  <label>Sl. No</label>
                  <input type="text" value={emp.slNo} onChange={(e) => handleEmployeeChange(idx, 'slNo', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Name</label>
                  <input type="text" value={emp.name} onChange={(e) => handleEmployeeChange(idx, 'name', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Designation</label>
                  <input type="text" value={emp.designation} onChange={(e) => handleEmployeeChange(idx, 'designation', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Normal Hours</label>
                  <input type="number" value={emp.normalHours} onChange={(e) => handleEmployeeChange(idx, 'normalHours', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Overtime Hours</label>
                  <input type="number" value={emp.overtimeHours} onChange={(e) => handleEmployeeChange(idx, 'overtimeHours', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Hourly Rate (₹)</label>
                  <input type="number" value={emp.hourlyRate} onChange={(e) => handleEmployeeChange(idx, 'hourlyRate', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Overtime Payment (₹)</label>
                  <input type="number" value={emp.overtimePay} onChange={(e) => handleEmployeeChange(idx, 'overtimePay', e.target.value)} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="form-section">
          <div className="section-header">
            <IndianRupee size={20} />
            <h3>Approval Details</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Prepared By</label>
              <input type="text" value={formData.preparedBy} onChange={(e) => handleInputChange('preparedBy', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Approved By</label>
              <input type="text" value={formData.approvedBy} onChange={(e) => handleInputChange('approvedBy', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Place</label>
              <input type="text" value={formData.place} onChange={(e) => handleInputChange('place', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Date</label>
              <DateInput value={formData.date} onChange={(e) => handleInputChange('date', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button onClick={generatePDF} disabled={isGenerating} className={`btn-generate ${isGenerating ? 'generating' : ''}`}>
            {isGenerating ? (
              <>
                <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" opacity="0.25"/>
                  <path d="M12 2a10 10 0 0 1 0 20" opacity="0.75"/>
                </svg>
                <span>Generating PDF...</span>
              </>
            ) : (
              <>
                <FileDown size={20} />
                <span>Generate & Download Overtime Register PDF</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormXXIIIOvertime;
